'use client'

import { useTransition } from 'react'
import { cn } from '@eversports/klimt-design-components/tailwind/utils'
import Button from '@eversports/klimt-primitives/alpha/Button'
import { useParams, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'

import { CLASSNAME_CONTAINED_CONTENT } from '../../../../../App.constants'
import ActivityDateGroup from '../components/ActivityDateGroup'
import getGraphqlForClient from '../../../../../lib/get-graphql-for-client'
import groupActivitiesByDate from '../helpers/group-activities-by-date'
import formatQueryParams from '../helpers/format-query-params'
import { EVENT_LIMIT, EVENT_TYPES } from '../get-page-data'
import usePaginatedData from '../../../../../lib/use-paginated-data'
import type { Locale } from '../../../../../../localization/i18n'

import Filters from './Filters'
import { EventBookableItem } from './ActivityCard/ActivityCard.types'
import { FILTER_KEY_URL_PARAMS } from './Filters/Filters.types'

interface Props {
  initialActivityData: Array<EventBookableItem>
  initialPageInfo: { hasNextPage: boolean; endCursor: string | null }
}

export default function ActivityResults({ initialActivityData, initialPageInfo }: Props) {
  const t = useTranslations('discover-sport-city')

  const [isPending, startTransition] = useTransition()

  const { city, sport, locale } = useParams<{ city: string; sport: string; locale: Locale }>()

  const searchParams = useSearchParams()

  const activityDataQuery = async ({ afterCursor }: { afterCursor: string | null }) => {
    // TODO - see if this can be done in the formatQueryParams itself
    // Problem is that the TYPE of this "searchParams" on the server is different than that returned by the useSearchParams() hook on the client
    const eventFiltersFromUrlParams = formatQueryParams({
      dateParam: searchParams.get(FILTER_KEY_URL_PARAMS.DATE),
      timeParam: searchParams.get(FILTER_KEY_URL_PARAMS.START_TIME),
      activityLocationParam: searchParams.getAll(FILTER_KEY_URL_PARAMS.ACTIVITY_LOCATION),
      levelParam: searchParams.getAll(FILTER_KEY_URL_PARAMS.LEVEL),
    })

    const response = await getGraphqlForClient().DiscoverActivitiesDiscoverContextByCityAndSportSlug({
      citySlug: city,
      sportSlug: sport,
      first: EVENT_LIMIT,
      after: afterCursor,
      eventTypes: EVENT_TYPES,
      ...eventFiltersFromUrlParams,
    })

    const activityData = response.discoverContextByCityAndSportSlug.eventBookableItems.nodes.filter(
      (eventBookableItem) => eventBookableItem.eventSessions?.[0],
    )

    const pageInfo = response.discoverContextByCityAndSportSlug.eventBookableItems.pageInfo

    return {
      data: activityData,
      pageInfo,
    }
  }

  const {
    data: activityData,
    pageInfo,
    isLoading,
    handleLoadMore,
  } = usePaginatedData<EventBookableItem>({
    initialData: initialActivityData,
    initialPageInfo,
    query: activityDataQuery,
  })

  if (!activityData.length) {
    return (
      <>
        <Filters startTransition={startTransition} locale={locale} />
        <div className="relative h-20 bg-surface-primary py-3">
          <div className={cn(CLASSNAME_CONTAINED_CONTENT, 'flex h-10 items-center justify-center font-medium')}>
            {t('no-results-headers')}
          </div>
          {/* TODO - this is a spinner -  need to create a component, and run it by Amir */}
          {isPending && (
            <div role="status" className="absolute left-1/2 top-[90px] z-10 -translate-x-1/2">
              <div className="size-6 animate-spin rounded-full border-4 border-solid border-border-brand-primary border-t-transparent"></div>
            </div>
          )}
        </div>
      </>
    )
  }

  const groupedActivities = groupActivitiesByDate(activityData)

  return (
    <>
      <Filters startTransition={startTransition} locale={locale} />
      <div className="relative border-t border-border-divider bg-surface-primary py-2">
        {/* TODO - this is a spinner -  need to create a component, and run it by Amir */}
        {isPending && (
          <div role="status" className="absolute left-1/2 top-4 z-10 -translate-x-1/2">
            <div className="size-6 animate-spin rounded-full border-4 border-solid border-border-brand-primary border-t-transparent"></div>
          </div>
        )}
        <div className={cn(CLASSNAME_CONTAINED_CONTENT, isPending && 'opacity-50', 'flex flex-col items-center')}>
          {groupedActivities.map((group, index) => (
            <ActivityDateGroup
              key={group.groupDate}
              groupDate={group.groupDate}
              locale={locale}
              activities={group.activities}
              hasPriority={index === 0}
            />
          ))}
          {pageInfo.hasNextPage && (
            <Button disabled={isPending || isLoading} onClick={handleLoadMore} className="mb-4 w-24">
              {/* TODO - this another smaller, spinner variant -  need to create a component, and run it by Amir */}
              {isLoading ? (
                <div role="status">
                  <div className="size-2 animate-spin rounded-full border border-solid border-white border-t-transparent"></div>
                </div>
              ) : (
                t('load-more')
              )}
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
